/**
 * Money and date formatting for the Accounting V2 expense surfaces.
 *
 * WHY THESE ARE TOTAL FUNCTIONS: Intl.NumberFormat throws a RangeError on an
 * invalid or empty currency code, and a default parameter only substitutes for
 * `undefined`, never for the empty string. A legacy or imported expense row with
 * `currency: ''` would therefore crash a tab mid-render. Every function here
 * returns a string for any input instead of throwing.
 */

const FALLBACK_CURRENCY = 'INR';

/** ISO 4217 codes are exactly three letters; anything else is unusable. */
function safeCurrency(currency?: string | null): string {
  const code = String(currency ?? '').trim().toUpperCase();
  return /^[A-Z]{3}$/.test(code) ? code : FALLBACK_CURRENCY;
}

/**
 * Format integer minor units (paise) as a currency string.
 * Non-finite amounts are treated as zero.
 */
export function formatMinor(minor: number | null | undefined, currency?: string | null): string {
  const raw = Number(minor);
  const amount = Number.isFinite(raw) ? raw / 100 : 0;
  const code = safeCurrency(currency);

  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: code,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${code} ${amount.toFixed(2)}`;
  }
}

/** Short date for table cells. Em dash for missing or invalid input. */
export function formatShortDate(value?: string | Date | null): string {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { year: '2-digit', month: 'short', day: '2-digit' });
}

/** Convert a major-unit string from a form input into integer minor units. */
export function toMinorUnits(majorValue: string | number): number {
  const n = typeof majorValue === 'number' ? majorValue : parseFloat(String(majorValue));
  if (!Number.isFinite(n)) return 0;
  // Round half-up on the scaled value to avoid float artefacts like 1.005 * 100.
  return Math.round(n * 100);
}

/** Convert integer minor units back to a major-unit string for a form input. */
export function toMajorInput(minor: number | null | undefined): string {
  const n = Number(minor);
  if (!Number.isFinite(n) || n === 0) return '';
  return (n / 100).toFixed(2);
}

/**
 * Mirror of the server's computeExpenseTax for live form preview only.
 *
 * The server recomputes every total on save and its result is authoritative;
 * this exists purely so the user can see the effect of their inputs before
 * submitting. Keep the branches in step with accounting/expenses/tax.js.
 */
export function previewExpenseTax(input: {
  netMinor: number;
  discountMinor?: number;
  taxRate?: number;
  treatment?: string;
  withholdingRate?: number;
}): { netMinor: number; taxMinor: number; grossMinor: number; withholdingMinor: number; payableMinor: number } {
  const base = Math.max(0, Math.round(input.netMinor) - Math.max(0, Math.round(input.discountMinor ?? 0)));
  const rate = Math.max(0, Number(input.taxRate) || 0);
  const treatment = input.treatment ?? 'exclusive';

  // Each branch produces its own net/tax/gross triple, so there are no
  // placeholder initialisers to be overwritten.
  const { netMinor, taxMinor, grossMinor } = ((): { netMinor: number; taxMinor: number; grossMinor: number } => {
    switch (treatment) {
      case 'zero_rated':
      case 'exempt':
        return { netMinor: base, taxMinor: 0, grossMinor: base };

      case 'inclusive': {
        const net = Math.round((base * 100) / (100 + rate));
        return { netMinor: net, taxMinor: base - net, grossMinor: base };
      }

      case 'reverse_charge':
        // The supplier is paid net only; the tax is self-assessed on both sides.
        return { netMinor: base, taxMinor: Math.round((base * rate) / 100), grossMinor: base };

      default: {
        const tax = Math.round((base * rate) / 100);
        return { netMinor: base, taxMinor: tax, grossMinor: base + tax };
      }
    }
  })();

  const wRate = Math.max(0, Number(input.withholdingRate) || 0);
  const withholdingMinor = wRate > 0 ? Math.round((netMinor * wRate) / 100) : 0;

  return { netMinor, taxMinor, grossMinor, withholdingMinor, payableMinor: grossMinor - withholdingMinor };
}
